/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { searchUrlBuilder } from '@/lib/url';
import { useTranslation } from 'react-i18next';

interface RecentSearchesProps {
    recentSearches?: string[];
    closeAndNavigate: (link: string) => void;
    clearRecentSearches: () => void;
}

export default function RecentSearches({
    recentSearches = [],
    closeAndNavigate,
    clearRecentSearches,
}: RecentSearchesProps) {
    const { t } = useTranslation('search');
    return (
        <div>
            {recentSearches.length > 0 && (
                <div>
                    <div className="mb-4 text-xs font-medium tracking-wide text-brand-gray-550">
                        {t('suggestions.recentSearches')}
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {recentSearches.map((recentSearch) => (
                            <button
                                key={recentSearch}
                                type="button"
                                data-slot="suggestion"
                                onMouseDown={() => {
                                    closeAndNavigate(searchUrlBuilder(recentSearch));
                                }}
                                className="w-fit bg-header-menu-icon-hover-background px-4 py-2 text-left text-xs font-normal transition-colors hover:bg-brand-black hover:text-brand-white">
                                {recentSearch}
                            </button>
                        ))}
                    </div>
                    <button
                        type="button"
                        data-slot="suggestion"
                        onMouseDown={clearRecentSearches}
                        className="mt-4 w-fit text-left text-xs font-normal hover:underline">
                        {t('suggestions.clearRecentSearches')}
                    </button>
                </div>
            )}
        </div>
    );
}
